import { getDateByObject } from 'utils/date';
import { clientRoles, PERSON_TYPE } from 'constants/globals';

import type {
  OnboardingFieldConfig,
  StepFieldsConfig,
} from 'components/NewClient/Onboarding/config';

import type {
  EconomicInformationValues,
  GeneralDataValues,
  BankAccountValues,
} from './models';

const resolveFieldValue = (
  value: any,
  config: OnboardingFieldConfig,
): string => {
  if (config.inputType === 'text') {
    return value || '';
  }
  return value?.value || '';
};

export const permanentResidencyValidation = (values: any) => {
  const { nationality: nationalityVal, residenceY: residenceYearsVal } = values;
  return nationalityVal !== '117' && +residenceYearsVal < 5;
};

export const generalDataRemapper = (
  values: GeneralDataValues,
  permanentResidency: boolean,
  clientRole: string,
  dataStepGeneral: any,
) => {
  const homeAddress = {
    addressId: values.addressId || null,
    areaStreet: values.areaStreet,
    areaExteriorNumber: values.areaExteriorNumber,
    areaInteriorNumber: values.areaInteriorNumber,
    zipCode: values.zipCode,
    areaSuburb: values.areaSuburb && values.areaSuburb.label,
    cveLocation: values.cveLocation,
    areaCity: values.areaCity,
    areaState: values.areaState,
    cveState: values.areaState,
    cvePais: values.cvePais && values.cvePais.label,
    addressType: 'HOME_ADDRESS',
  };

  let foreignAddress: any = {
    addressId: values.foreignAddressId || null,
    areaStreet: values.foreignAreaStreet,
    cvePais: values.foreignCvePais && values.foreignCvePais.label,
    addressType: 'FOREIGN_ADDRESS',
  };

  if (values.isForeignPerson) {
    foreignAddress = {
      ...foreignAddress,
      areaState: values.foreignAreaState,
      cveState: values.foreignAreaState,
      zipCode: values.foreignZipCode,
    };
  } else {
    foreignAddress = {
      ...foreignAddress,
      cveState: values.foreignAreaState && values.foreignAreaState.value,
      areaState: values.foreignAreaState && values.foreignAreaState.label,
    };
  }

  const fiscalAddress = {
    addressId: values.fiscalAddressId || null,
    areaStreet: values.fiscalAreaStreet,
    areaExteriorNumber: values.fiscalAreaExteriorNumber,
    areaInteriorNumber: values.fiscalAreaInteriorNumber,
    zipCode: values.fiscalZipCode,
    areaSuburb: values.fiscalAreaSuburb && values.fiscalAreaSuburb.label,
    cveLocation: values.fiscalCveLocation,
    areaCity: values.fiscalAreaCity,
    areaState: values.fiscalAreaState,
    cveState: values.fiscalAreaState,
    cvePais: values.fiscalCvePais && values.fiscalCvePais.label,
    addressType: 'FISCAL_ADDRESS',
  };

  let coOwnerData: any = {
    coOwnerId: null,
  };

  if (clientRole === clientRoles.CO_OWNER) {
    coOwnerData = {
      coOwnerId: values.coOwnerId || null,
      clientId: dataStepGeneral.clientId,
      contractId: dataStepGeneral.contractId,
      clientRole,
    };
  }

  return {
    createdBy: '',
    registrationDate: null,
    updateBy: null,
    updateDate: null,
    clientRole,
    titularId: dataStepGeneral.clientId,
    contractId: dataStepGeneral.contractId,
    clientId:
      clientRole === clientRoles.CLIENT ? dataStepGeneral.clientId : null,
    cveTypePersonFiscal: PERSON_TYPE.fisica,
    cveInternal: values.cveInternal,
    curp: values.curp,
    rfc: values.rfc,
    name: values.name,
    fathersLastName: values.fathersLastName,
    mothersLastName: values.mothersLastName,
    additionalNationality:
      (values.additionalNationality && values.additionalNationality.label) ||
      '',
    birthCountry: values.birthCountry && values.birthCountry.value,
    birthDate: getDateByObject({
      date: values.birthDate,
      requiredReplaceSeparator: true,
    }),
    openingDate: getDateByObject({
      date: values.openingDate,
      format: 'yyyy-MM-dd',
    }),
    cancellationDate: getDateByObject({
      date: values.cancellationDate,
      format: 'yyyy-MM-dd',
    }),
    birthState:
      values.birthState && typeof values.birthState === 'object'
        ? values.birthState.value
        : values.birthState,
    civilStatus: values.civilStatus && values.civilStatus.value,
    cellPhone: values.cellPhone,
    homePhone: values.homePhone,
    companyPhone: values.companyPhone,
    email1: values.email1,
    gender: values.gender && values.gender.value,
    houseType: values.houseT && values.houseT.value,
    nationality: values.nationality && values.nationality.value,
    taxResidenteEUA: values.taxResidenteEUA && values.taxResidenteEUA.value,
    addresses: [homeAddress, fiscalAddress, foreignAddress],
    permanentResidency,
    residenceYears: values.residenceY,
    yearsResidenceMexico: values.yearsResidenceMexico,
    isBlacklist: false,
    isPep: false,
    isForeignPerson: values.isForeignPerson,
    cveTypeClient: values.cveTypeClient?.value || null,
    ...coOwnerData,
  };
};

export const economicInfoDataRemapper = (
  values: EconomicInformationValues,
  dataStepGeneral: GeneralDataValues,
  clientRole: string,
  fieldsConfig: StepFieldsConfig<'fisica', 'economicInfo'>,
) => {
  let coOwnerData = {};

  if (clientRole === clientRoles.CO_OWNER) {
    coOwnerData = {
      coOwnerId: values.coOwnerId || null,
      clientId: dataStepGeneral.clientId,
      contractId: dataStepGeneral.contractId,
      clientRole,
    };
  }

  return {
    ...values,
    updateBy: null,
    coOwnerId: 0,
    clientId: dataStepGeneral.clientId,
    contractId: dataStepGeneral.contractId,
    actualPosition: resolveFieldValue(
      values.actualPosition,
      fieldsConfig.actualPosition,
    ),
    companyActivity: values.companyActivity?.value || '',
    companyField: values.companyField?.value || '',
    companySector: values.companySector?.value || '',
    companyShare: values.companyShare?.value || '',
    economicActivity: values.economicActivity?.value || '',
    incomeAdditional: values.incomeAdditional?.value || '',
    incomeLevel: values.incomeLevel?.value || '',
    incomeSource: values.incomeSource?.value || '',
    publicServant: values.publicServant?.value || '',
    relativePublicServant: values.relativePublicServant?.value || '',
    publicServantSeniority: values.publicServantSeniority?.value || '',
    relativePublicServantRelationship:
      values.relativePublicServantRelationship?.value || '',
    relativePublicServantSeniority:
      values.relativePublicServantSeniority?.value || '',
    seniority: values.seniority || '',
    taxRegime: values.taxRegime && values.taxRegime.label,
    taxBurdenPercentage:
      parseFloat(`${values.taxBurdenPercentage || '0'}`) || 0,
    occupation: (values.occupation && values.occupation.value) || '',
    profession: resolveFieldValue(values.profession, fieldsConfig.profession),
    ...coOwnerData,
  };
};

export const bankAccountRemapper = (
  values: BankAccountValues,
  dataStepGeneral: GeneralDataValues,
  clientRole: string,
) => {
  let coOwnerData: any = {};

  if (clientRole === clientRoles.CO_OWNER) {
    coOwnerData = {
      coOwnerId: values.coOwnerId || null,
      clientId: dataStepGeneral.clientId,
      contractId: dataStepGeneral.contractId,
      clientRole,
    };
  }
  return {
    clientId: dataStepGeneral.clientId,
    contractId: dataStepGeneral.contractId,
    bankAccounts: values.bankAccounts?.map((item: any) => ({
      ...item,
      bank: (item.bank || '') && item.bank.label,
      clabe: item.clabe || '',
      plaza: item.plaza || '',
      branchOffice: item.branchOffice || '',
    })),
    ...coOwnerData,
  };
};
